import React, { useState, useEffect, useRef } from 'react';
import { Select, MenuItem, Box, IconButton, Button, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterFactory from 'components/filters/FilterFactory';

const ColumnFilterSelector = ({ 
  availableColumns,
  selectedColumnFilters = [],
  onChange,
  onValidationErrorChange,
  sourceTableSchema = [],
  projectId,
  datasetId,
  tableId
}) => {
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [filterValidationErrors, setFilterValidationErrors] = useState({});
  const prevValidationState = useRef(false);
  
  // Check for empty filters (first validation check)
  useEffect(() => {
    const hasErrors = selectedColumnFilters.some(col => 
      col.column && (!col.filter || col.filter.trim() === '')
    );
    
    setHasValidationErrors(hasErrors);
    
    // Only notify parent if the validation state has changed
    if (onValidationErrorChange && prevValidationState.current !== hasErrors) {
      onValidationErrorChange(hasErrors);
      prevValidationState.current = hasErrors;
    }
  }, [selectedColumnFilters, onValidationErrorChange]);

  const handleFilterValidationError = (index, hasError) => {
    setFilterValidationErrors(prev => {
      // Only update if the error state for this index has changed
      if (prev[index] === hasError) {
        return prev;
      }
      return { ...prev, [index]: hasError };
    });
  };

  // Combined validation check (second validation check)
  useEffect(() => {
    // Calculate the new validation state
    const hasAnyValidationError = Object.values(filterValidationErrors).some(error => error) || 
                                 selectedColumnFilters.some(col => col.column && (!col.filter || col.filter.trim() === ''));
    
    // Only notify parent if the validation state has actually changed
    if (onValidationErrorChange && prevValidationState.current !== hasAnyValidationError) {
      onValidationErrorChange(hasAnyValidationError);
      prevValidationState.current = hasAnyValidationError;
    }
  }, [filterValidationErrors, selectedColumnFilters, onValidationErrorChange]);

  const handleAddColumn = () => {
    const newColumns = [...selectedColumnFilters, { 
      column: '', 
      filter: '', 
      filterMode: ''
    }];
    onChange(newColumns);
  };

  const handleColumnChange = (index, newColumn) => {
    const updatedColumns = [...selectedColumnFilters];
    updatedColumns[index].column = newColumn;
    updatedColumns[index].filter = '';
    
    // Get the column type
    const columnType = getFieldType(newColumn);
    
    // Determine the appropriate default filter mode based on column type
    const friendlyTypes = [
      'integer',
      'float',
      'numeric',
      'date',
      'datetime',
      'timestamp',
      'bool',
      'boolean'
    ];
    
    // Default to 'friendly' mode for non-string columns that support it
    if (columnType !== 'string' && friendlyTypes.includes(columnType.toLowerCase())) {
      updatedColumns[index].filterMode = 'friendly';
    } else {
      // Default to 'sql' mode for string columns or types without friendly UI
      updatedColumns[index].filterMode = 'sql';
    }
    
    onChange(updatedColumns);
  };

  const handleFilterChange = (index, newFilter, filterMode) => {
    const updatedColumns = [...selectedColumnFilters];
    updatedColumns[index] = {
      ...updatedColumns[index],
      filter: newFilter,
      filterMode: filterMode
    };
    onChange(updatedColumns);
  };

  const handleDeleteColumn = (index) => {
    const updatedColumns = selectedColumnFilters.filter((_, i) => i !== index);
    onChange(updatedColumns);
    
    // Also clean up the validation errors state when removing a filter
    setFilterValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      
      // Reindex the keys to match the new array indices
      const reindexed = {};
      Object.keys(newErrors).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > index) {
          reindexed[numKey - 1] = newErrors[key];
        } else {
          reindexed[key] = newErrors[key];
        }
      });
      
      return reindexed;
    });
  };

  const availableOptions = (selectedColumn) => 
    availableColumns.filter(
      (col) => col === selectedColumn || !selectedColumnFilters.some((selected) => selected.column === col)
    );

  // Helper function to get field_type for a column name using the sourceTableSchema
  const getFieldType = (columnName) => {
    const column = sourceTableSchema.find(col => col.name === columnName);
    return column ? column.field_type.toLowerCase() : 'string';
  };

  return (
    <div>
      {hasValidationErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please specify filters for all selected columns.
        </Alert>
      )}
      {selectedColumnFilters.map((item, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Select
              value={item.column}
              onChange={(e) => handleColumnChange(index, e.target.value)}
              displayEmpty
              fullWidth
              error={Boolean(item.column && !item.filter)}
            >
              <MenuItem value="" disabled>
                Select a column
              </MenuItem>
              {availableOptions(item.column).map((column) => (
                <MenuItem key={column} value={column}>
                  {column}
                </MenuItem>
              ))}
            </Select>
            {item.column && (
              <FilterFactory
                columnType={getFieldType(item.column)}
                value={item.filter}
                onChange={(newFilter, mode) => handleFilterChange(index, newFilter, mode)}
                error={Boolean(item.column && !item.filter)}
                onValidationErrorChange={(hasError) => handleFilterValidationError(index, hasError)}
                projectId={projectId}
                datasetId={datasetId}
                tableId={tableId}
                columnName={item.column}
                initialFilterMode={item.filterMode}
              />
            )}
          </Box>
          <IconButton 
            onClick={() => handleDeleteColumn(index)}
            color="error"
            sx={{ mt: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Button onClick={handleAddColumn} variant="contained" color="primary">
        + Add Column
      </Button>
    </div>
  );
};

export default ColumnFilterSelector; 